import { Modal, Button, Form, Alert } from "react-bootstrap"
import { flagIcon, uploadIcon } from "../../constants/icons"
import { submitReport } from "../../api/user/report.api"
import { useState } from "react"
import { useDispatch } from "react-redux"
import {
  setToastBoxDescription,
  setToastBoxStatus,
} from "../../redux/ToastBoxSlice"

function ReportErrorModal({ show, handleClose, questionId, questionText }) {
  const [detail, setDetail] = useState("")
  const [screenshot, setScreenshot] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const dispatch = useDispatch()
  const maxFileSize = 3 * 1024 * 1024 // 3MB

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) {
      setScreenshot(null)
      return
    }

    const allowedTypes = ["image/jpeg", "image/png"]

    if (!allowedTypes.includes(file.type)) {
      dispatch(setToastBoxStatus(true))
      dispatch(
        setToastBoxDescription(
          "Invalid file type. Please upload a JPG, JPEG or PNG image."
        )
      )
      console.warn("Invalid file type")
      setScreenshot(null)
      return
    }

    if (file.size > maxFileSize) {
      dispatch(setToastBoxStatus(true))
      dispatch(
        setToastBoxDescription(
          "File size exceeds 3MB. Please upload a smaller file."
        )
      )
      console.warn("File too large")
      setScreenshot(null)
      return
    }

    setScreenshot(file)
  }

  const handleSubmit = async () => {
    setError(null)
    setSuccess(null)
    if (!detail.trim()) {
      setError("Please provide details for the report.")
      return
    }
    setLoading(true)
    try {
      await submitReport(detail, screenshot, questionId)
      setSuccess("Report submitted successfully!")
      setDetail("")
      setScreenshot(null)

      setTimeout(() => {
        setSuccess(null) // Clear success message
        setError(null) // Clear error message
        handleClose()
      }, 1500)
    } catch (err) {
      console.error("Failed to submit report:", err)

      setError(
        err.response?.data?.message ||
          "Failed to submit report. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    setDetail("")
    setScreenshot(null)
    setLoading(false)
    setError(null)
    setSuccess(null)
    handleClose()
  }

  return (
    <Modal
      className='common-modal'
      show={show}
      onHide={handleModalClose}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className='modal-report-title'>{flagIcon} Report error</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <h5 className='modal-question'>{questionText}</h5>
        {success && <Alert variant='success'>{success}</Alert>}
        {error && <Alert variant='danger'>{error}</Alert>}

        <Form>
          <Form.Group controlId='errorDetails'>
            <Form.Label>Give us details about this content</Form.Label>
            <Form.Control
              as='textarea'
              rows={4}
              placeholder='Write the details here'
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group controlId='screenshotUpload' className='mt-3'>
            <Form.Label>Attach a screenshot (optional)</Form.Label>

            <div className='upload-box'>
              <Form.Label htmlFor='uploadInput' className='upload-label'>
                <div className='upload-icon'>{uploadIcon}</div>

                <h4>
                  <span>Click to upload</span> or drag and drop
                </h4>

                {screenshot && (
                  <p className='small text-primary'>{screenshot.name}</p>
                )}

                <p className='small'>JPG, JPEG or PNG (File size max: 3MB)</p>
              </Form.Label>

              <Form.Control
                id='uploadInput'
                type='file'
                accept='.jpg,.jpeg,.png'
                className='d-none'
                onChange={handleFileChange}
                disabled={loading}
              />
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='light' onClick={handleModalClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          variant='danger'
          onClick={handleSubmit}
          disabled={loading || !detail.trim()}
        >
          {loading && (
            <span
              className='spinner-border spinner-border-sm me-2'
              role='status'
              aria-hidden='true'
            ></span>
          )}
          Submit report
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ReportErrorModal
